/**
 * News for the instrument on screen.
 *
 * The chart answers "what is the price doing". This answers "and why might it
 * be", for the same symbol, without leaving the chart — which is the whole
 * argument for a co-pilot that sits beside a trading platform rather than a tab
 * somebody remembers to check.
 *
 * ## Chronological, not ranked
 *
 * The Fundamentals feed is ranked against the trader's journal, because "what
 * matters to me today" is a question about them. This is a different question —
 * *what has just happened to this contract* — and ranking it would put a
 * three-hour-old critical release above a headline from four minutes ago.
 * Reverse-chronological is the correct answer here and `order: 'time'` asks for
 * it explicitly.
 *
 * `symbol` resolves server-side into the instrument's own topics: its root, its
 * parent for a micro, and its currencies. A GBPJPY chart gets the Bank of
 * England and the Bank of Japan and no US macro at all.
 */
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { api, type FeedEntry } from '../lib/api.js';

/** How long ago, in the shortest form that is still unambiguous. */
function ago(iso: string, now: number): string {
  const ms = now - Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export function SymbolNews({ symbol, limit = 6 }: { symbol: string; limit?: number }): JSX.Element | null {
  const [entries, setEntries] = useState<FeedEntry[] | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // Per-effect liveness, never a shared ref: under StrictMode a shared one is
    // set false by the first cleanup and never restored, and every update after
    // that is silently dropped.
    let live = true;
    const load = async (): Promise<void> => {
      try {
        const r = await api.news({ symbol, limit, order: 'time' });
        if (live) setEntries(r.entries);
      } catch {
        // A failed poll leaves the last good list on screen. An error bar
        // beside a chart for a strip that is not the point of the screen is
        // noise the trader has to dismiss.
        if (live) setEntries((prev) => prev ?? []);
      }
    };
    void load();
    const poll = setInterval(() => { void load(); }, 60_000);
    const clock = setInterval(() => { setNow(Date.now()); }, 30_000);
    return () => { live = false; clearInterval(poll); clearInterval(clock); };
  }, [symbol, limit]);

  // Renders nothing at all until there is something to say. A permanently empty
  // panel beside a chart is worse than no panel: it costs vertical space on
  // every symbol that has no coverage.
  if (entries === null || entries.length === 0) return null;

  return (
    <section className="sym-news" aria-label={`News affecting ${symbol}`}>
      <h3 className="panel-h">NEWS · {symbol}</h3>
      <ul className="sym-news-list">
        {entries.map((e) => {
          const critical = (e.kind === 'release' ? e.card.importance : e.item.importance) === 'critical';
          const at = e.kind === 'release' ? e.card.releasedAt : e.item.publishedAt;
          const src = e.kind === 'release' ? e.card.sourceId : e.item.sourceId;
          const text = e.kind === 'release' ? e.card.headline : e.item.title;
          const key = e.kind === 'release' ? `${e.card.sourceId}:${e.card.referencePeriod}` : e.item.id;
          return (
            <li key={key} className={`sym-news-row${critical ? ' critical' : ''}`}>
              <span className="sym-news-age mono">{ago(at, now)}</span>
              {e.kind === 'item' ? (
                <a className="sym-news-text" href={e.item.url} target="_blank" rel="noreferrer noopener"
                  title={`${src} · ${new Date(at).toLocaleString()}`}>
                  {text}
                </a>
              ) : (
                // A release has nowhere to link: the number is the content.
                <span className="sym-news-text" title={`${src} · ${new Date(at).toLocaleString()}`}>
                  {text}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
